import React, { useState, useEffect } from "react";
import { userRequest } from "../../../utils/requestMethods";
import { generateSchoolSessions } from "../../../utils/schoolYearUtils";
import { toast } from "react-toastify";
import { useStateContext } from "../../../contexts/ContextProvider";

const toLocalDateTimeInput = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 16);
};

const EditTestModal = ({
  closeModal,
  school,
  subjects: propsSubjects,
  test,
  onTestUpdated,
  isAdminMode: propsAdminMode,
}) => {
  const { user } = useStateContext();
  const isAdminMode = propsAdminMode || user?.role === 0;
  const [subjects, setSubjects] = useState(propsSubjects || []);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const isSharedTest = Boolean(
    test?.class_group_id ||
    test?.class_group ||
    test?.is_shared_across_group ||
    test?.is_group_subject,
  );

  const [formData, setFormData] = useState({
    title: test?.title || "",
    description: test?.description || "",
    duration: test?.duration || 0,
    total_score: test?.total_score || 0,
    school: test?.school?._id || test?.school || school?._id || "",
    class: test?.class?._id || test?.class || "",
    subject: test?.subject?._id || test?.subject || test?.subject_id || "",
    term: test?.term || school?.settings?.current_term || 1,
    session: test?.session || school?.settings?.current_session || "",
    type: test?.type || "",
    ca: test?.ca || 0,
    category: test?.category || "assessment",
    scoring_mode: test?.scoring_mode || "full",
    mcq_max_score: test?.mcq_max_score || 0,
    theory_max_score: test?.theory_max_score || 0,
    start_date: toLocalDateTimeInput(test?.start_date),
    end_date: toLocalDateTimeInput(test?.end_date),
  });

  const schoolSessions = generateSchoolSessions(2022, 20);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Fetch assigned subjects if not provided as props (teacher mode only, for single class tests)
  useEffect(() => {
    if (isAdminMode || isSharedTest) return; // Shared tests or admin mode use test subject directly

    const fetchAssignedSubjects = async () => {
      if (propsSubjects && propsSubjects.length > 0) {
        // setSubjects(propsSubjects.filter(s => s.teacher_role === 'primary'));
        setSubjects(propsSubjects);
        return;
      }

      if (!school?._id || !user?._id) return;

      setSubjectsLoading(true);
      try {
        const { data } = await userRequest.get(
          `/teachers/teacher-assigned-subjects/${school?._id}/${user?._id}?limit=100`,
        );
        // setSubjects((data.subjects || []).filter(s => s.teacher_role === 'primary'));
        setSubjects(data.subjects || []);
      } catch (error) {
        console.error("Error fetching assigned subjects:", error);
        setSubjects([]);
      } finally {
        setSubjectsLoading(false);
      }
    };

    fetchAssignedSubjects();
  }, [school?._id, user?._id, propsSubjects, isAdminMode, isSharedTest]);

  useEffect(() => {
    if (test) {
      setFormData({
        title: test.title || "",
        description: test.description || "",
        duration: test.duration || 0,
        total_score: test.total_score || 0,
        school: test.school?._id || test.school || school?._id || "",
        class: test.class?._id || test.class || "",
        subject: test.subject?._id || test.subject || test.subject_id || "",
        term: test.term || school?.settings?.current_term || 1,
        session: test.session || school?.settings?.current_session || "",
        type: test.type || "",
        ca: test.ca || 0,
        category: test.category || "assessment",
        scoring_mode: test.scoring_mode || "full",
        mcq_max_score: test.mcq_max_score || 0,
        theory_max_score: test.theory_max_score || 0,
        start_date: toLocalDateTimeInput(test.start_date),
        end_date: toLocalDateTimeInput(test.end_date),
      });
    }
  }, [test, school]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericFields = [
      "duration",
      "total_score",
      "ca",
      "term",
      "mcq_max_score",
      "theory_max_score",
    ];
    const isNumeric = numericFields.includes(name);

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: isNumeric ? Number(value) : value,
      };

      // When scoring mode changes to "full", reset partial scoring fields
      if (name === "scoring_mode" && value === "full") {
        updated.mcq_max_score = 0;
        updated.theory_max_score = 0;
      }

      // Auto-compute theory_max_score when mcq_max_score changes
      if (name === "mcq_max_score") {
        updated.theory_max_score = Math.max(0, updated.total_score - Number(value));
      }

      if (name === "total_score") {
        if (updated.scoring_mode === "partial_mcq" && updated.mcq_max_score > 0) {
          updated.theory_max_score = Math.max(0, Number(value) - updated.mcq_max_score);
        }
      }

      // When type changes, reset scoring mode to default if not exam
      if (name === "type" && value !== "exam") {
        updated.scoring_mode = "full";
        updated.mcq_max_score = 0;
        updated.theory_max_score = 0;
      }

      return updated;
    });
  };

  const handleSubjectChange = (selectedOption) => {
    const selectedSubject = subjects.find((subj) => subj._id === selectedOption.value);

    const caOptions = getCAOptionsForSubject(selectedSubject);
    const shouldAutoSelectCA = formData.type === "ca" && caOptions.length === 1;

    const updates = {
      subject: selectedOption.value,
      class: selectedSubject?.class?._id || "",
      ca: shouldAutoSelectCA ? 1 : formData.ca,
    };

    if (formData.category === "assessment") {
      const assessmentSystem = getAssessmentSystemForSubject(selectedSubject);
      if (formData.type === "exam") {
        updates.total_score = assessmentSystem?.exam?.max_score || 100;
      } else if (formData.type === "ca") {
        const caIdx = shouldAutoSelectCA ? 0 : formData.ca > 0 ? formData.ca - 1 : null;
        if (caIdx !== null && caOptions[caIdx]) {
          updates.total_score = caOptions[caIdx].max_score;
        }
      }
    }

    setFormData((prevData) => ({ ...prevData, ...updates }));
  };

  // Get assessment system based on subject's class level of education
  const getAssessmentSystemForSubject = (subject) => {
    const levelOfEducation =
      subject?.class?.level_of_education ||
      subject?.level_of_education ||
      test?.class?.level_of_education ||
      test?.class_group?.level_of_education;

    const assessmentSettings = school?.settings?.assessment;
    const systems = assessmentSettings?.assessment_system;

    if (Array.isArray(systems) && systems.length > 0) {
      if (levelOfEducation) {
        const matched = systems.find(
          (config) =>
            String(config.level_of_education).toLowerCase() ===
            String(levelOfEducation).toLowerCase(),
        );
        if (matched) return matched;
      }

      // Fallback to default assessment system
      const defaultConfig = systems.find((config) => config.is_default === true);
      if (defaultConfig) return defaultConfig;
      return systems[0];
    }

    if (assessmentSettings?.ca?.cas || assessmentSettings?.exam) {
      return assessmentSettings;
    }

    return { ca: { cas: [] }, exam: { max_score: 100 } };
  };

  // Get CA options based on subject's class level of education
  const getCAOptionsForSubject = (subject) => {
    const assessmentSystem = getAssessmentSystemForSubject(subject);
    const cas = assessmentSystem?.ca?.cas;
    if (Array.isArray(cas) && cas.length > 0) {
      return cas.map((ca, idx) => ({
        title: ca.title || ca.name || `CA ${idx + 1}`,
        max_score: ca.max_score ?? ca.score ?? 20,
      }));
    }
    return [];
  };

  // Get CA options for the currently selected subject
  const getCurrentCAOptions = () => {
    const selectedSubject =
      subjects.find((subj) => String(subj._id) === String(formData.subject)) ||
      (typeof test?.subject === "object" ? test.subject : null) ||
      test;
    return getCAOptionsForSubject(selectedSubject);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.description || !formData.total_score || !formData.duration) {
      setError("Please fill in all required fields.");
      setTimeout(() => setError(""), 5000);
      return;
    }

    // Validate partial scoring mode (only for exams)
    if (formData.type === "exam" && formData.scoring_mode === "partial_mcq") {
      if (!formData.mcq_max_score || formData.mcq_max_score <= 0) {
        setError("MCQ Max Score is required when using partial scoring mode.");
        setTimeout(() => setError(""), 5000);
        return;
      }
      if (formData.mcq_max_score >= formData.total_score) {
        setError("MCQ Max Score must be less than the Total Score.");
        setTimeout(() => setError(""), 5000);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      // A shared test has no single class — formData.class was seeded from test.class._id,
      // which for a shared test is the class GROUP's id (enriched server-side for display),
      // not a real class. Sending it back would corrupt the test's class_group_id scoping.
      if (test?.class_group_id) {
        delete payload.class;
        delete payload.subject;
      }
      if (payload.category === "admission") {
        if (!payload.subject) {
          delete payload.subject;
        }
        delete payload.ca;
      }
      if (payload.category === "learning") {
        delete payload.ca;
      }
      if (payload.type === "exam") {
        delete payload.ca;
      }
      // Convert non-empty datetime-local values to ISO strings, remove empty ones
      if (payload.start_date) {
        payload.start_date = new Date(payload.start_date).toISOString();
      } else {
        delete payload.start_date;
      }
      if (payload.end_date) {
        payload.end_date = new Date(payload.end_date).toISOString();
      } else {
        delete payload.end_date;
      }
      const { data } = await userRequest.put(`/test/${test._id}`, payload);
      setSuccess(true);
      toast.success("Test updated successfully!");
      setTimeout(() => {
        setSuccess(false);
        closeModal();
        if (onTestUpdated) onTestUpdated(data);
      }, 1500);
    } catch (error) {
      console.error(error);
      setError("An error occurred while updating the test.");
      toast.error("Failed to update test");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Test</h2>
            <p className="text-gray-600 text-sm mt-1">Update test settings and configuration</p>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex items-center justify-center"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Status Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-500 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-red-700 font-medium">{error}</span>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-green-500 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-green-700 font-medium">Test updated successfully!</span>
              </div>
            </div>
          )}

          <form onSubmit={submitHandler} className="space-y-4">
            {/* Category and Test Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={(e) => {
                    const val = e.target.value;
                    const validTypes =
                      val === "assessment"
                        ? ["ca", "exam"]
                        : val === "admission"
                          ? ["exam", "mock", "practice"]
                          : ["practice", "mock", "homework"];
                    setFormData((prev) => {
                      const nextType = validTypes.includes(prev.type) ? prev.type : "";
                      return {
                        ...prev,
                        category: val,
                        type: nextType,
                        ca: nextType === "ca" ? prev.ca : 0,
                      };
                    });
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                >
                  <option value="assessment">Assessment</option>
                  <option value="learning">Learning</option>
                  <option value="admission">Admission</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Test Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    if (newType === "exam" && formData.category === "assessment") {
                      const selectedSubject =
                        subjects.find((subj) => String(subj._id) === String(formData.subject)) ||
                        (typeof test?.subject === "object" ? test.subject : null) ||
                        test;
                      if (selectedSubject) {
                        const assessmentSystem = getAssessmentSystemForSubject(selectedSubject);
                        setFormData((prev) => ({
                          ...prev,
                          type: newType,
                          total_score: assessmentSystem?.exam?.max_score || prev.total_score || 100,
                        }));
                        return;
                      }
                    }
                    setFormData((prev) => ({
                      ...prev,
                      type: newType,
                      scoring_mode: newType !== "exam" ? "full" : prev.scoring_mode,
                      mcq_max_score: newType !== "exam" ? 0 : prev.mcq_max_score,
                      theory_max_score: newType !== "exam" ? 0 : prev.theory_max_score,
                    }));
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                >
                  <option value="">Select test type</option>
                  {formData.category === "assessment" ? (
                    <>
                      <option value="ca">CA (Continuous Assessment)</option>
                      <option value="exam">Exam</option>
                    </>
                  ) : formData.category === "admission" ? (
                    <>
                      <option value="exam">Exam</option>
                      <option value="mock">Mock Exam</option>
                      <option value="practice">Practice Test</option>
                    </>
                  ) : (
                    <>
                      <option value="practice">Practice Test</option>
                      <option value="mock">Mock Exam</option>
                      <option value="homework">Homework</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            {/* Subject and Class */}
            {formData.category === "admission" ? (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Class</label>
                <input
                  type="text"
                  value={test?.class?.class_name || ""}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed"
                  disabled
                />
              </div>
            ) : isAdminMode || isSharedTest ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Subject{" "}
                    {isSharedTest && (
                      <span className="text-xs text-indigo-600 font-semibold">
                        (Shared Subject)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={
                      test?.subject?.subject_name ||
                      test?.shared_subject?.subject_name ||
                      subjects.find((s) => String(s._id) === String(formData.subject))
                        ?.subject_name ||
                      "Shared Subject"
                    }
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed font-medium"
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {isSharedTest ? "Class Group" : "Class"}
                  </label>
                  <input
                    type="text"
                    value={
                      test?.class_group?.name ||
                      test?.class?.class_name ||
                      (isSharedTest
                        ? "Class Group"
                        : subjects.find((s) => String(s._id) === String(formData.subject))?.class
                            ?.class_name || "")
                    }
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed font-medium"
                    disabled
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={(e) => handleSubjectChange({ value: e.target.value })}
                    disabled={subjectsLoading}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {subjectsLoading ? "Loading subjects..." : "Choose a subject"}
                    </option>
                    {test?.subject &&
                      !subjects.some(
                        (s) => String(s._id) === String(test.subject?._id || test.subject),
                      ) && (
                        <option value={test.subject?._id || test.subject}>
                          {test.subject?.subject_name || "Current Subject"}
                        </option>
                      )}
                    {subjects.map((subject) => (
                      <option key={subject._id} value={subject._id}>
                        {subject.subject_name} ({subject.class?.class_name}) -{" "}
                        {subject.teacher_role}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Class</label>
                  <input
                    type="text"
                    value={
                      subjects.find((s) => String(s._id) === String(formData.subject))?.class
                        ?.class_name ||
                      test?.class?.class_name ||
                      ""
                    }
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed"
                    disabled
                  />
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Test Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter test title"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe what this test covers"
                rows={3}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 resize-none"
                required
              />
            </div>

            {/* CA Number (only show if type is CA) */}
            {formData.type === "ca" && (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">CA Number</label>
                <select
                  name="ca"
                  value={formData.ca}
                  onChange={(e) => {
                    handleChange(e);
                    // Auto-populate total score when CA is selected
                    if (formData.category === "assessment" && e.target.value !== "0") {
                      const caOptions = getCurrentCAOptions();
                      const selectedCA = caOptions[parseInt(e.target.value) - 1];
                      if (selectedCA) {
                        setFormData((prev) => ({ ...prev, total_score: selectedCA.max_score }));
                      }
                    }
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  disabled={!formData.subject}
                >
                  <option value="0">
                    {!formData.subject
                      ? "Select a subject first"
                      : getCurrentCAOptions().length === 0
                        ? "No CA options available"
                        : "Select CA"}
                  </option>
                  {getCurrentCAOptions().map((ca, index) => (
                    <option key={index} value={index + 1}>
                      {ca.title || `CA ${index + 1}`} ({ca.max_score} marks)
                    </option>
                  ))}
                </select>
                {formData.subject && getCurrentCAOptions().length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    No CA configuration found for this subject's class level. Please check school
                    assessment settings.
                  </p>
                )}
              </div>
            )}

            {/* Duration and Score */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Duration (minutes) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="e.g., 60"
                  min="1"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Total Score <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="total_score"
                  value={formData.total_score}
                  onChange={handleChange}
                  placeholder="e.g., 100"
                  min="1"
                  className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${
                    formData.category === "assessment" &&
                    (formData.type === "ca" || formData.type === "exam")
                      ? "cursor-not-allowed"
                      : ""
                  }`}
                  disabled={
                    formData.category === "assessment" &&
                    (formData.type === "ca" || formData.type === "exam")
                  }
                  required
                />
                {formData.category === "assessment" &&
                  (formData.type === "ca" || formData.type === "exam") && (
                    <p className="text-xs text-slate-500 mt-1">
                      Auto-set based on selected {formData.type === "ca" ? "CA" : "exam"}{" "}
                      configuration
                    </p>
                  )}
              </div>
            </div>

            {/* Scoring Mode - Only show for Exam type */}
            {formData.type === "exam" && (
              <div className="border-t border-gray-200 pt-4">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Scoring Mode <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose how exam scores are calculated. This only affects exam-type tests.
                  </p>
                  <div className="space-y-3">
                    <label
                      className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-300"
                      style={{
                        borderColor: formData.scoring_mode === "full" ? "#3b82f6" : "#e5e7eb",
                      }}
                    >
                      <input
                        type="radio"
                        name="scoring_mode"
                        value="full"
                        checked={formData.scoring_mode === "full"}
                        onChange={handleChange}
                        className="mt-0.5 mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Full Auto-Grading</div>
                        <div className="text-xs text-gray-500 mt-1">
                          All questions are auto-graded up to the full total score of the exam. The
                          total score you set is used as the maximum mark for computing each
                          student's result.
                        </div>
                      </div>
                    </label>
                    {/* Partial auto-grading disabled until backend issue is resolved */}
                    <label
                      className="flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-blue-50 hover:border-blue-300"
                      style={{
                        borderColor:
                          formData.scoring_mode === "partial_mcq" ? "#3b82f6" : "#e5e7eb",
                      }}
                    >
                      <input
                        type="radio"
                        name="scoring_mode"
                        value="partial_mcq"
                        checked={formData.scoring_mode === "partial_mcq"}
                        onChange={handleChange}
                        className="mt-0.5 mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Partial Auto-Grading</div>
                        <div className="text-xs text-gray-500 mt-1">
                          You assign a custom score for the test instead of using the default from
                          your school's assessment settings. The student's result for the test is
                          computed based on the marks you allocate here.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Partial Scoring Fields - Only show when partial_mcq is selected */}
                {formData.scoring_mode === "partial_mcq" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-center mb-2">
                      <svg
                        className="w-5 h-5 text-blue-600 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-blue-900">
                        Partial Scoring Configuration
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Enter the max score for this test. Students' results will be computed over
                      this mark. The remaining marks ({formData.total_score} − MCQ) can be added to
                      the approved result later.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Exam Max Score <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="mcq_max_score"
                        value={formData.mcq_max_score}
                        onChange={handleChange}
                        placeholder="e.g., 60"
                        min="1"
                        max={formData.total_score - 1}
                        className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        required={formData.scoring_mode === "partial_mcq"}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Students' scores for this test will be computed over this mark
                      </p>
                    </div>
                    {formData.mcq_max_score > 0 && (
                      <div className="text-xs font-medium mt-2 p-2 rounded bg-green-100 text-green-700">
                        MCQ: {formData.mcq_max_score} marks (auto-graded) &middot; Remaining:{" "}
                        {formData.total_score - formData.mcq_max_score} marks (manual grading)
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Start Date and End Date (Optional) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Start Date & Time
                </label>
                <input
                  type="datetime-local"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Students can begin the test from this date and time. Leave empty to allow access
                  immediately once published.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  End Date & Time
                </label>
                <input
                  type="datetime-local"
                  name="end_date"
                  value={formData.end_date}
                  min={formData.start_date || undefined}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  No student can start or submit the test after this date and time. Leave empty for
                  no deadline.
                </p>
              </div>
            </div>

            {/* Term and Session - Automatically set from school settings */}
            {/*<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Term</label>
                <select
                  name="term"
                  value={formData.term}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                >
                  <option value="">Select term</option>
                  <option value="1">First Term</option>
                  <option value="2">Second Term</option>
                  <option value="3">Third Term</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Academic Session</label>
                <select
                  name="session"
                  value={formData.session}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                >
                  <option value="">Select session</option>
                  {schoolSessions.map((session) => (
                    <option key={session.value} value={session.value}>
                      {session.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>*/}
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-600 border border-gray-300 font-semibold rounded-full px-5 py-2 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={submitHandler}
              disabled={loading}
              className="text-white bg-blue-700 hover:bg-blue-800 disabled:bg-blue-400 font-medium rounded-full px-5 py-2.5 transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Test"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTestModal;
